export type CrmAnalyticsLayout = {
  order: string[];
  minimized: string[];
  hidden: string[];
};

const STORAGE_PREFIX = "wembii.crm.analytics.layout";

export const CRM_ANALYTICS_DEFAULT_ORDER = [
  "crmChartOpportunitiesByDay",
  "crmChartActivitiesByDay",
  "cardsByList",
  "cardsByStatus",
  "cardsByUser",
  "budgetByList",
  "budgetByStatus",
  "averageBudgetByList",
  "winRateByList",
  "dealExpiration",
  "commentsByUser",
  "tasksByUser",
] as const;

export type CrmAnalyticsWidgetId =
  (typeof CRM_ANALYTICS_DEFAULT_ORDER)[number];

const defaultLayout = (): CrmAnalyticsLayout => ({
  order: [...CRM_ANALYTICS_DEFAULT_ORDER],
  minimized: [],
  hidden: [],
});

const storageKey = (companyId?: string, boardId?: string) =>
  `${STORAGE_PREFIX}:${companyId || "unknown"}:${boardId || "unknown"}`;

const sanitizeLayout = (
  raw: Partial<CrmAnalyticsLayout> | null | undefined
): CrmAnalyticsLayout => {
  const base = defaultLayout();
  const known = new Set<string>(CRM_ANALYTICS_DEFAULT_ORDER);
  const orderFromRaw = Array.isArray(raw?.order)
    ? raw!.order.filter((id) => known.has(id))
    : [];
  const missing = CRM_ANALYTICS_DEFAULT_ORDER.filter(
    (id) => !orderFromRaw.includes(id)
  );
  const order = [...orderFromRaw, ...missing];
  const minimized = Array.isArray(raw?.minimized)
    ? raw!.minimized.filter((id) => known.has(id))
    : [];
  const hidden = Array.isArray(raw?.hidden)
    ? raw!.hidden.filter((id) => known.has(id))
    : [];

  return {
    order,
    minimized,
    hidden,
  };
};

export const loadCrmAnalyticsLayout = (
  companyId?: string,
  boardId?: string
): CrmAnalyticsLayout => {
  try {
    const raw = localStorage.getItem(storageKey(companyId, boardId));
    if (!raw) return defaultLayout();
    return sanitizeLayout(JSON.parse(raw) as Partial<CrmAnalyticsLayout>);
  } catch {
    return defaultLayout();
  }
};

export const saveCrmAnalyticsLayout = (
  companyId: string | undefined,
  boardId: string | undefined,
  layout: CrmAnalyticsLayout
) => {
  try {
    localStorage.setItem(
      storageKey(companyId, boardId),
      JSON.stringify(sanitizeLayout(layout))
    );
  } catch {
    /* ignore quota / private mode */
  }
};

export const resetCrmAnalyticsLayout = (
  companyId?: string,
  boardId?: string
): CrmAnalyticsLayout => {
  const next = defaultLayout();
  saveCrmAnalyticsLayout(companyId, boardId, next);
  return next;
};

export const reorderCrmAnalyticsWidgets = (
  order: string[],
  fromId: string,
  toId: string
): string[] => {
  if (fromId === toId) return order;
  const next = [...order];
  const fromIndex = next.indexOf(fromId);
  const toIndex = next.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0) return order;
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, fromId);
  return next;
};
